import { nanoid } from 'nanoid';
import { type ReactNode, useEffect, useRef } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { subscribe, useSnapshot } from 'valtio';
import { EditorMode } from '~/common/types';
import EditorSurfaceCanvas from '~/components2/Map/canvas/EditorSurface';
import { mapState } from '~/components2/Map/Map';
import { useCivilDrawStore } from '../civilDrawStoreContext';

export default function EditorSurface() {
  const store = useCivilDrawStore();
  const map = useMap();

  const containerDiv = useRef<HTMLDivElement>(null);

  const { project, editorMode } = useSnapshot(store) as typeof store;

  const content: ReactNode = null;

  // HTML map markers sit above this canvas, so a click on one would open its record instead of
  // drawing. The class drives a stylesheet rule that makes them click-through; mapbox re-asserts
  // each marker's inline pointer-events every frame, so an inline style cannot hold.
  useEffect(() => {
    const container = map.current?.getMap().getContainer();
    if (!container || editorMode === EditorMode.None) {
      return;
    }
    container.classList.add('map-tool-active');
    return () => {
      container.classList.remove('map-tool-active');
    };
  }, [editorMode, map]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (containerDiv.current && map.current && editorMode !== EditorMode.None) {
      const surface = new EditorSurfaceCanvas({
        id: `editor-surface-${nanoid()}`,
        container: containerDiv.current,
        map: map.current.getMap(),
        store: store,
      });

      const unsubscribe = subscribe(mapState, () => surface.refreshScreenCoords());
      return () => {
        unsubscribe();
        surface.destroy();
      };
    }
  }, [editorMode]);

  if (!map.current) {
    return null;
  }

  if (project.isLoading === true) {
    return null;
  }

  if (editorMode === EditorMode.None) {
    return null;
  }

  return (
    <div style={{ cursor: '-moz-grab' }} className={'absolute left-0 top-0'} ref={containerDiv}>
      {content}
    </div>
  );
}
